import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineCodeBracket,
  HiOutlinePaintBrush,
  HiOutlineDevicePhoneMobile,
  HiOutlineVideoCamera,
  HiOutlineRocketLaunch,
  HiOutlineLightBulb,
} from 'react-icons/hi2';
import { useServices, useSettings } from '../hooks/useApi';
import { useLanguage } from '../context/LanguageContext';

const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/api$/, '');

const FALLBACK_ICONS = [
  <HiOutlineCodeBracket />,
  <HiOutlinePaintBrush />,
  <HiOutlineDevicePhoneMobile />,
  <HiOutlineVideoCamera />,
  <HiOutlineRocketLaunch />,
  <HiOutlineLightBulb />,
];

const FALLBACK_SERVICES = [
  { title: 'Web Development', description: 'Modern, responsive websites and web applications built with the latest technologies.' },
  { title: 'UI/UX Design', description: 'User-centered design that combines aesthetics with functionality.' },
  { title: 'Mobile Applications', description: 'Native and cross-platform mobile apps that deliver seamless experiences.' },
  { title: 'Interactive Installations', description: 'Immersive on-ground experiences using projection mapping, touch screens, sensors.' },
  { title: 'Virtual Experiences', description: 'Virtual events, 3D environments, and metaverse solutions.' },
  { title: 'AI & Innovation', description: 'Cutting-edge AI integrations, machine learning solutions.' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

function ServiceCard({ service, idx, inView, onNavigate, cardHeight, getField }) {
  const videoRef = useRef(null);
  const [cardRef, cardInView] = useInView({ threshold: 0.3, triggerOnce: false });
  const cardVideo = service.cardVideoUrl || service.videoUrl || '';
  const hasVideo = !!cardVideo;

  // Auto-play when card scrolls into view
  useEffect(() => {
    if (!videoRef.current) return;
    if (cardInView) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [cardInView]);

  return (
    <motion.div
      ref={cardRef}
      className={`services__card ${hasVideo ? 'services__card--has-video services__card--playing' : ''}`}
      custom={idx}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={cardVariants}
      onClick={() => onNavigate(service)}
      style={{ cursor: 'pointer', minHeight: `${cardHeight}px` }}
    >
      {/* Video Background - always visible */}
      {hasVideo && (
        <div className="services__card-video-wrapper">
          <video
            ref={videoRef}
            className="services__card-video"
            src={cardVideo.startsWith('http') ? cardVideo : `${API_URL}${cardVideo}`}
            muted
            autoPlay
            loop
            playsInline
            preload="auto"
          />
          <div className="services__card-video-overlay" />
        </div>
      )}

      {/* Content */}
      <div className="services__card-content">
        <div className="services__card-icon">
          {service.icon ? (
            <i className={service.icon} style={{ fontSize: 24 }} />
          ) : (
            FALLBACK_ICONS[idx % FALLBACK_ICONS.length]
          )}
        </div>
        <h3 className="services__card-title">{getField(service, 'title') || service.name}</h3>
        <p className="services__card-desc">{(getField(service, 'description') || '').replace(/<[^>]*>/g, '')}</p>
      </div>
    </motion.div>
  );
}

function ServicesSection() {
  const { t, getField } = useLanguage();
  const { data: servicesData } = useServices();
  const { data: settings } = useSettings();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const navigate = useNavigate();

  const services = (Array.isArray(servicesData) ? servicesData : servicesData?.data || FALLBACK_SERVICES);
  const globalCardHeight = Number(settings?.service_card_height) || 280;

  const handleNavigate = (service) => {
    const slug = (service.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    navigate(`/services/${slug}`, { state: { service } });
  };

  return (
    <section id="services" className="services">
      <div className="container">
        <motion.div
          className="section__header"
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="section__label">{t('services.label')}</span>
          <h2 className="section__title">{t('services.title')}</h2>
          <div className="divider" />
          <p className="section__subtitle">
            {t('services.subtitle')}
          </p>
        </motion.div>

        <div className="services__grid">
          {services.map((service, idx) => (
            <ServiceCard
              key={service._id || service.title || idx}
              service={service}
              idx={idx}
              inView={inView}
              onNavigate={handleNavigate}
              cardHeight={globalCardHeight}
              getField={getField}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default ServicesSection;
